<template>
    <div class="tablewrapper" :class="[className, { 'table-compact-selection': compactSelection && showSelect, 'table-bordered': bordered, 'table-no-cell-padding': noCellPadding, 'table-disable-row-hover': disableRowHover }]">
        <v-card-text class="controls" v-if="showTableControls">
            <TableControls
                ref="tableControls"
                :filters-conf="filtersConf"
                :filters="filters"
                :views-scope="resolvedViewsScope"
                :views-exclude-keys="viewsExcludeKeys"
                :enable-ordering="enableOrdering"
                :exclude-from-ordering="excludeFromOrdering"
                :columns="columns"
                :enable-column-management="enableColumnManagement"
                :columns-scope="resolvedColumnsScope"
                :quick-filter-count="quickFilterCount"
                :force-filters-panel-open="forceFiltersPanelOpen"
                :search-placeholder="searchPlaceholder"
                :autofocus-search="autofocusSearch"
                @on-filters-change="onFiltersChange($event)"
                @view-apply="onViewApply"
                @view-reset="onViewReset"
                @columns-state-change="onColumnsStateChange"
            />
        </v-card-text>
        <div class="table-bulk-selection-wrapper" v-if="value?.length && actions?.length">
            <Block class="table-bulk-selection-bar">
                <div class="table-bulk-selection-bar__message black--text">
                    <span
                        >{{ $translate("youSelected") }}
                        <span class="font-weight-bold">
                            <template v-if="value?.length == 1">
                                1 {{ $translate("item") }}
                            </template>
                            <template v-else>
                                {{ value.length }}
                                {{ $translate("items") }}</template
                            >.
                        </span>
                        <template v-if="value.length == 1">
                            {{ $translate("selectionItemInstructions") }}
                        </template>
                        <template v-else>
                            {{ $translate("selectionItemsInstructions") }}
                        </template>
                    </span>
                </div>
                <v-btn
                    @click="clearSelection"
                    class="table-bulk-selection-bar__clear font-weigth-regular"
                    text
                >
                    <v-icon class="mr-1">{{ iconName("cancel") }}</v-icon>
                    {{ $translate("clearSelection") }}
                </v-btn>
                <Action
                    class="table-bulk-selection-bar__action"
                    :label="$translate('selectAction')"
                    :main="true"
                    :items="actions"
                    :loading="bulkLoading"
                    :disabled="bulkLoading"
                    @on-action="runBulkAction"
                />
            </Block>
        </div>
        <Placeholder
            v-if="errors?.length"
            :title="$translate('messages.errorLoadingData')"
            icon="nodata"
            :description="$translate('messages.errorLoadingDataDescription')"
            :actions="[
                {
                    text: $translate('buttons.retry'),
                    action: 'retry',
                },
            ]"
            @on-action="onErrorAction"
        />
        <template v-else-if="showEmptyPlaceholder">
            <Placeholder
                :icon="placeholderIcon"
                :title="placeholderTitle || $translate('messages.noResultFound')"
                :description="
                    placeholderDescription || $translate('messages.noResultFoundDescription')
                "
                :actions="allPlaceholderActions"
                @on-action="onPlaceholderAction"
            />
        </template>
        <TableSkeleton
            v-else-if="showTableSkeleton"
            :column-count="skeletonColumnCount"
            :row-count="skeletonRowCount"
            :show-select="showSelect"
            :dense="dense"
            :show-footer="!hideDefaultFooter"
            :header-labels="skeletonHeaderLabels"
        />
        <v-data-table
            v-else-if="showDataTable"
            :value="value"
            @input="onSelectionInput"
            @item-selected="$emit('item-selected')"
            @toggle-select-all="$emit('toggle-select-all')"
            :headers="headers"
            :dense="dense"
            :mobile-breakpoint="0"
            :items="rows || []"
            :loading="dataTableLoading"
            :page.sync="page"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :must-sort="mustSort"
            :single-select="singleSelect"
            :disabled="false"
            :selectable-key="selectableKey"
            :item-key="itemKey"
            :item-value="itemValue"
            :show-select="showSelect"
            v-bind="remoteServerBind"
            :items-per-page.sync="itemsPerPage"
            :no-results-text="placeholderTitle || $translate('messages.noResultFound')"
            :no-data-text="placeholderTitle || $translate('messages.noResultFound')"
            :footer-props="tableFooterProps"
            :item-class="itemClass"
            :hide-default-footer="hideDefaultFooter"
            :hide-default-header="hideDefaultHeader"
        >
            <template v-slot:item="{ item, headers, index }">
                <tr
                    :key="index"
                    :class="[
                        item[itemClass],
                        {
                            pointer: clickableRow,
                            'table-row--dragging': rowDragIndex === index,
                            'table-row--drag-over-before':
                                rowDragOverIndex === index && !rowDragPlaceAfter,
                            'table-row--drag-over-after':
                                rowDragOverIndex === index && rowDragPlaceAfter,
                        },
                    ]"
                    @dragover.prevent="onRowDragOver($event, index)"
                    @drop.prevent="onRowDrop($event, index)"
                    @dragenter.prevent
                    @dragend="onRowDragEnd"
                    @click="handleRowClick($event, item)"
                    @mouseenter="$emit('row-mouseenter', item)"
                    @mouseleave="$emit('row-mouseleave', item)"
                >
                    <td
                        v-for="header in headers"
                        :key="header.value || header.field || header.slot"
                        :class="[
                            'mb-0 mb-sm-0',
                            header.class,
                            { 'table-compact-select-cell': compactSelection && header.value === 'data-table-select', 'table-compact-action-cell': compactSelection && showSelect && ['catalog_entry_add', 'product_add', 'order_line_remove'].includes(header.field) },
                            {
                                'table-bordered-cell': bordered,
                                'sticky-column': header.sticky,
                                'table-cell-wrap': header.cellWrap === true,
                            },
                        ]"
                        :style="tdColumnStyle(header)"
                        :data-no-row-click="header.value == '_rowReorder' || undefined"
                    >
                        <slot
                            :name="header.slot || header.field"
                            :row="item"
                            :header="header"
                            :row-index="index"
                            :value="resolve(item, header.field)"
                        >
                            <template v-if="header.value == '_rowReorder'">
                                <span
                                    class="table-row-drag-handle"
                                    data-no-row-click
                                    draggable="true"
                                    @click.stop
                                    @mousedown.stop
                                    @dragstart="onRowDragStart($event, index)"
                                    @dragend="onRowDragEnd"
                                >
                                    <v-icon small color="grey darken-1">
                                        mdi-drag-vertical
                                    </v-icon>
                                </span>
                            </template>

                            <div v-else-if="header.type == 'actions'" class="actions">
                                <template v-if="header.actions">
                                    <CellAction
                                        v-for="action in header.actions"
                                        :key="action.action"
                                        :icon="action.icon"
                                        :type="action.type"
                                        :label="action.label"
                                        :color="action.color"
                                        :disabled="action.disabled"
                                        :placeholder="action.placeholder"
                                        @click.stop.prevent="handleAction(action, item)"
                                    />
                                </template>
                            </div>

                            <template v-else-if="header.value == 'data-table-select'">
                                <v-checkbox
                                    v-if="!selectableKey || item[selectableKey]"
                                    class="mt-0 pt-0"
                                    hide-details
                                    single-line
                                    outlined
                                    dense
                                    :key="`${item.id}-${isSelected(item)}`"
                                    :input-value="
                                        isSelected(item) || alreadySelectedFunc(item)
                                    "
                                    @click.stop
                                    @change="toggleItem(item, $event)"
                                ></v-checkbox>
                            </template>

                            <div v-else-if="header.type == 'getactions'" class="actions">
                                <CellAction
                                    v-for="action in header.get_actions(item)"
                                    :key="action.action"
                                    :icon="action.icon"
                                    :type="action.type"
                                    :label="action.label"
                                    :color="action.color"
                                    :disabled="action.disabled"
                                    :placeholder="action.placeholder"
                                    @click.stop.prevent="handleAction(action, item)"
                                />
                            </div>

                            <template v-else-if="header.displayIndex">{{ index + 1 }}</template>

                            <template v-else-if="header.type == 'number'">{{
                                number(resolve(item, header))
                            }}</template>

                            <template v-else-if="header.component">
                                <component v-bind:is="header.component" :item="item" />
                            </template>

                            <template v-else-if="header.type == 'boolean'">
                                <span :key="resolve(item, header)">
                                    <span v-if="resolve(item, header)">{{
                                        $translate("yes")
                                    }}</span>
                                    <span v-else>{{ $translate("no") }}</span>
                                </span>
                            </template>

                            <template v-else-if="header.type == 'email'">
                                <a
                                    @click.prevent
                                    :href="`mailto:${resolve(item, header)}`"
                                    >{{ resolve(item, header) }}</a
                                >
                            </template>

                            <template
                                v-else-if="
                                    header.subtype == 'euros' || header.type == 'euros'
                                "
                                ><template v-if="resolve(item, header) !== null">
                                    {{ price(resolve(item, header)) }}</template
                                >
                            </template>

                            <template
                                v-else-if="
                                    header.type == 'months' && resolve(item, header)
                                "
                            >
                                <span>
                                    {{
                                        resolve(item, header) > 1
                                            ? $translate("months", {
                                                  months: resolve(item, header),
                                              })
                                            : $translate("month", {
                                                  months: resolve(item, header),
                                              })
                                    }}
                                </span>
                            </template>

                            <template
                                v-else-if="
                                    header.type == 'percentage' ||
                                    header.type == 'percent'
                                "
                            >
                                <template v-if="resolve(item, header) !== null">
                                    {{ percent(percentValue(resolve(item, header))) }}
                                </template>
                                <template v-else>
                                    <em class="tableplaceholder">-</em>
                                </template>
                            </template>

                            <template v-else-if="resolve(item, header) !== null">{{
                                resolve(item, header)
                            }}</template>
                            <template v-else-if="header.placeHolder">
                                <em class="tableplaceholder">{{ header.placeHolder }}</em>
                            </template>
                        </slot>
                    </td>
                </tr>
            </template>
            <template v-if="summaryTotals" slot="body.append">
                <tr class="table-summary-row">
                    <td
                        v-for="(header, hi) in headers"
                        :key="'summary-' + header.value"
                        :class="[
                            'mb-0 mb-sm-0',
                            header.class,
                            { 'table-compact-select-cell': compactSelection && header.value === 'data-table-select', 'table-compact-action-cell': compactSelection && showSelect && ['catalog_entry_add', 'product_add', 'order_line_remove'].includes(header.field) },
                            {
                                'table-bordered-cell': bordered,
                                'table-cell-wrap': header.cellWrap === true,
                            },
                        ]"
                        :style="tdColumnStyle(header)"
                    >
                        <strong
                            v-if="
                                hi === summaryLabelColumnIndex &&
                                summaryTotals._firstCell
                            "
                        >
                            {{ summaryTotals._firstCell }}
                        </strong>
                        <slot
                            v-else-if="hasSummarySlot(header)"
                            :name="'summary-' + (header.slot || header.value)"
                            :header="header"
                            :summary-totals="summaryTotals"
                        />
                        <template v-else-if="summaryCellText(header) !== null">
                            {{ summaryCellText(header) }}
                        </template>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import Vue from "vue";
import CellAction from "./cell-action";
import TableControls from "./table-controls";
import TableSkeleton from "./table-skeleton";
import GlobalMixins from "@/mixins/global";
import { Decimal } from "decimal.js";

export default {
    name: "Table",
    components: {
        CellAction,
        TableControls,
        TableSkeleton,
    },
    mixins: [GlobalMixins],
    props: {
        compactSelection: { type: Boolean, default: false },
        value: {
            required: false,
        },
        bulkAction: {
            required: false,
            type: Function,
            default: () => {
                return () => Promise.resolve();
            },
        },

        placeholderIcon: {
            default: "noresult",
            type: String,
            required: false,
        },
        placeholderTitle: {
            type: String,
            required: false,
        },
        placeholderDescription: {
            type: String,
            required: false,
        },
        placeholderActions: {
            type: Array,
            required: false,
        },
        className: {
            required: false,
        },
        rows: {
            required: true,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        showTitle: {
            type: Boolean,
            required: false,
            default: false,
        },
        enableOrdering: {
            type: Boolean,
            default: false,
        },
        enableRowReorder: {
            type: Boolean,
            default: false,
        },
        filtersConf: {
            required: false,
            default: () => [],
        },
        /**
         * Effective query filters. Tab/section overlays are not handled here —
         * merge them in Vuex via `mergeUserAndSectionFilters` (stores/utils) and
         * pass the result (or a UI-stripped variant without tab-owned keys).
         */
        filters: {
            type: Object,
            required: false,
            default: () => {
                return {};
            },
        },
        viewsScope: {
            type: String,
            required: false,
            default: null,
        },
        /** When true (default), saved views are enabled using viewsScope, tableScope, or route name. */
        enableViews: {
            type: Boolean,
            default: true,
        },
        /** Explicit table scope for views/columns when viewsScope is not set. */
        tableScope: {
            type: String,
            default: null,
        },
        viewsExcludeKeys: {
            type: Array,
            required: false,
            default: () => [],
        },
        enableColumnManagement: {
            type: Boolean,
            default: true,
        },
        columnsScope: {
            type: String,
            default: null,
        },
        forceFiltersPanelOpen: {
            type: Boolean,
            default: false,
        },
        quickFilterCount: {
            type: Number,
            default: 3,
        },
        searchPlaceholder: {
            type: String,
            default: null,
        },
        /** Autofocus the search field. Also enabled when filtersConf search has `focus: true`. */
        autofocusSearch: {
            type: Boolean,
            default: false,
        },
        columns: {
            required: true,
        },
        title: {
            type: [String, Boolean],
            required: false,
        },
        itemClass: {
            required: false,
        },
        rowsCount: {
            required: true,
            default: null,
        },
        /** Expected row count while loading (skeleton only; does not affect hasTableData). */
        skeletonRows: {
            type: Number,
            required: false,
            default: null,
        },
        clickableRow: {
            required: false,
            type: Boolean,
            default: true,
        },
        errors: {
            required: false,
            type: Array,
            default: () => [],
        },
        showSelect: {
            required: false,
            type: Boolean,
            default: false,
        },
        itemKey: {
            required: false,
            type: String,
            default: "id",
        },
        selectableKey: {
            required: false,
            type: String,
            default: null,
        },
        itemValue: {
            required: false,
            type: String,
            default: null,
        },
        remote: {
            required: false,
            type: Boolean,
            default: true,
        },
        /** When true, v-data-table requires an active sort column (default). */
        mustSort: {
            type: Boolean,
            default: true,
        },
        hideDefaultHeader: {
            required: false,
            type: Boolean,
            default: false,
        },

        hideDefaultFooter: {
            required: false,
            type: Boolean,
            default: false,
        },
        /** Optional keyed totals row (keys = column field values; _firstCell = label in first data column). */
        summaryTotals: {
            type: Object,
            default: null,
        },
        alreadySelectedFunc: {
            required: false,
            type: Function,
            default: () => {
                return false;
            },
        },
        dense: {
            required: false,
            type: Boolean,
            default: false,
        },
        actions: {
            required: false,
            type: Array,
            default: () => [],
        },
        bulkLoading: {
            required: false,
            type: Boolean,
            default: false,
        },
        singleSelect: {
            required: false,
            type: Boolean,
            default: false,
        },
        bordered: {
            required: false,
            type: Boolean,
            default: false,
        },
        noCellPadding: {
            required: false,
            type: Boolean,
            default: false,
        },
        disableRowHover: {
            required: false,
            type: Boolean,
            default: false,
        },
    },
    computed: {
        allPlaceholderActions() {
            return [...(this.placeholderActions || []), {
                text: this.$translate('buttons.resetFilters'),
                action: 'reset-filters',
            }];
        },
        hasHiddenFilters() {
            return !!this.filtersConf.filter((filter) => filter != "search").length;
        },
        resolvedViewsScope() {
            if (!this.enableViews) {
                return null;
            }
            if (this.viewsScope) {
                return this.viewsScope;
            }
            if (this.tableScope) {
                return this.tableScope;
            }
            const routeName = this.$route?.name;
            return routeName ? String(routeName) : null;
        },
        resolvedColumnsScope() {
            return this.columnsScope || this.resolvedViewsScope;
        },
        showTableControls() {
            return this.filtersConf.length > 0;
        },
        effectiveColumns() {
            return this.orderedColumns || this.columns;
        },
        tableFooterProps() {
            if (this.hideDefaultFooter) return null;
            return {
                "items-per-page-options": [10, 20, 25, 30, 40, 50, 100, 200],
                "items-per-page-text": this.$translate("labels.itemsPerPage"),
            };
        },
        /** Omit server-items-length when remote=false so Vuetify applies client-side sorting. */
        remoteServerBind() {
            if (!this.remote) {
                return {};
            }
            return { serverItemsLength: this.rowsCount };
        },
        excludeFromOrdering() {
            return this.effectiveColumns.map((column) => column.sortField || column.field);
        },
        filtersItemsPerPage() {
            return (this.filters ? this.filters.limit : 20) || 20;
        },
        skeletonColumnCount() {
            const count = this.headers?.length || this.effectiveColumns?.length || 0;
            return Math.max(count, 4);
        },
        skeletonRowCount() {
            if (this.skeletonRows > 0) {
                return this.skeletonRows;
            }
            return 10;
        },
        skeletonHeaderLabels() {
            if (!this.headers?.length) return null;
            return this.headers
                .filter((h) => h.value !== "data-table-select")
                .map((h) => h.text || "");
        },
        hasVisibleRows() {
            return Array.isArray(this.rows) && this.rows.length > 0;
        },
        hasTableData() {
            return this.rowsCount > 0 || this.hasVisibleRows;
        },
        showTableSkeleton() {
            return this.loading && !this.hasTableData;
        },
        showEmptyPlaceholder() {
            return !this.loading && !this.hasTableData;
        },
        showDataTable() {
            return this.hasTableData;
        },
        dataTableLoading() {
            return this.loading && this.hasTableData;
        },

        /**
         * When the column menu is active, visibility comes from column state.
         * `column.hide` is only the default for that breakpoint, so a column
         * the user turns on is actually rendered.
         */
        columnPickerOwnsVisibility() {
            return this.enableColumnManagement && Array.isArray(this.orderedColumns);
        },
        headers() {
            let displayed_columns = this.effectiveColumns.filter((column) => {
                if (this.columnPickerOwnsVisibility) {
                    return true;
                }
                return (
                    !column.hide ||
                    column.hide.indexOf(this.$vuetify.breakpoint.name) == -1
                );
            });

            const mapped = displayed_columns
                .map((item) => {
                    if (item.type == "_orderingfield") {
                        if (!this.filters.ordering) {
                            return null;
                        }
                        // Get ordering field without leading '-'
                        let rawfield = this.filters.ordering;
                        rawfield = rawfield.slice(rawfield.lastIndexOf("-") + 1);
                        // Do not take ordering field column into account if already displayed on columns
                        if (
                            displayed_columns.find(
                                (finditem) => finditem.field == rawfield
                            )
                        ) {
                            return null;
                        }
                        item.field = this.filters.ordering;
                        item.label = this.$translate("labels.orderFilter");
                        item.sortable = true;
                    }

                    let column = Object.assign(item, {
                        text: item.label,
                        sticky: item.sticky || false,
                        sortable: item.sortable !== false,
                        value: item.sortField || item.field,
                    });
                    return column;
                })
                .filter((item) => item);
            if (this.enableRowReorder) {
                mapped.unshift({
                    text: "",
                    value: "_rowReorder",
                    field: "_rowReorder",
                    sortable: false,
                    width: "40px",
                    class: "table-row-reorder-col",
                });
            }
            return mapped;
        },
        displayFilters() {
            return true;
        },
        summaryLabelColumnIndex() {
            const hs = this.headers || [];
            const idx = hs.findIndex(
                (h) => h && h.value && h.value !== "data-table-select"
            );
            return idx >= 0 ? idx : 0;
        },
    },
    data() {
        // Compound orderings like "-allowed,owner_name,name": use the first field only
        // for the header indicator (same as filters.ordering watcher).
        let filtersSortBy = null;
        let filtersSortDesc = false;
        if (this.filters?.ordering) {
            const raw = String(this.filters.ordering);
            filtersSortDesc = raw.charAt(0) === "-";
            filtersSortBy = raw.replace(/^-/, "").split(",")[0] || null;
        }
        // Hidden footer: show every row (no client-side page slice).
        // Prefer filters.limit so the footer select matches the first remote fetch
        // (must be a value present in items-per-page-options).
        const itemsPerPageInitial = this.hideDefaultFooter
            ? -1
            : !this.remote
                ? this.rows?.length
                : this.filters?.limit || 20;
        let data = {
            sortBy: filtersSortBy ? [filtersSortBy] : [],
            sortDesc: filtersSortBy ? [filtersSortDesc] : [],
            itemsPerPage: itemsPerPageInitial,
            page: this.filters.limit
                ? parseInt((this.filters.offset || 0) / this.filters.limit) + 1
                : 1,
            orderedColumns: null,
            // Ignore Vuetify sort-by.sync noise until after first mount.
            sortSyncReady: false,
            rowDragIndex: null,
            rowDragOverIndex: null,
            // true = insert after the hovered row (bottom half), false = before (top half)
            rowDragPlaceAfter: false,
            rowReorderDragging: false,
        };
        return data;
    },
    watch: {
        page: {
            handler(page) {
                if (this.hideDefaultFooter) {
                    return;
                }
                let filters = Object.assign({ limit: this.itemsPerPage }, this.filters);
                filters.offset = (page - 1) * this.filters.limit;
                this.$emit("on-filters-change", filters);
            },
        },
        "filters.limit": {
            handler(limit) {
                if (this.hideDefaultFooter) {
                    this.itemsPerPage = -1;
                    return;
                }
                this.itemsPerPage = limit;
            },
        },
        itemsPerPage: {
            handler(itemsPerPage) {
                if (this.hideDefaultFooter) {
                    return;
                }
                if (!this.sortSyncReady) {
                    return;
                }
                let filters = Object.assign({}, this.filters);
                filters.limit = itemsPerPage;
                filters.offset = 0;
                this.page = 1;
                this.$emit("on-filters-change", filters);
            },
        },
        sortBy: {
            handler(sortBy) {
                if (!this.sortSyncReady) {
                    return;
                }
                let filters = Object.assign({}, this.filters);
                filters.ordering = this.getOrdering();
                filters.offset = 0;
                this.page = 1;
                if (
                    filters.ordering === this.filters.ordering
                    && Number(this.filters.offset || 0) === 0
                ) {
                    return;
                }
                this.$emit("on-filters-change", filters);
            },
        },
        sortDesc: {
            handler(sortDesc) {
                if (!this.sortSyncReady) {
                    return;
                }
                let filters = Object.assign({}, this.filters);
                filters.ordering = this.getOrdering();
                if (filters.ordering === this.filters.ordering) {
                    return;
                }
                this.$emit("on-filters-change", filters);
            },
        },
        "filters.ordering"(ordering) {
            if (!this.sortSyncReady) {
                return;
            }
            if (this.getOrdering() === ordering) {
                return;
            }
            const raw = ordering || "";
            const desc = raw.charAt(0) === "-";
            const field = raw.replace(/^-/, "").split(",")[0] || null;
            this.sortBy = field ? [field] : [];
            this.sortDesc = field ? [desc] : [];
        },
        headers: {
            handler() {
                this.$nextTick(() => {
                    this.applyStickyHeaders();
                });
            },
            deep: true,
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.sortSyncReady = true;
            this.applyStickyHeaders();
        });
    },
    updated() {
        this.$nextTick(() => {
            this.applyStickyHeaders();
        });
    },
    methods: {
        handleRowClick(event, item) {
            if (!this.clickableRow || this.rowReorderDragging) {
                return;
            }
            const target = event?.target;
            if (
                target instanceof Element
                && target.closest(
                    "a, button, .link, label, input, textarea, select, .v-input, [data-no-row-click]",
                )
            ) {
                return;
            }
            this.$emit("on-row-click", item);
        },
        summaryCellText(header) {
            if (!this.summaryTotals || !header) {
                return null;
            }
            const v = this.summaryTotals[header.value];
            if (v === undefined || v === null || v === "") {
                return null;
            }
            return v;
        },
        hasSummarySlot(header) {
            if (!header) {
                return false;
            }
            const name = "summary-" + (header.slot || header.value);
            return !!(this.$scopedSlots && this.$scopedSlots[name]);
        },
        tdColumnStyle(header) {
            if (!header) {
                return {};
            }
            if (this.compactSelection && header.value === 'data-table-select') {
                return { width: '36px', minWidth: '36px', maxWidth: '36px' };
            }
            const style = {};
            if (header.minWidth) {
                style.minWidth = header.minWidth;
            }
            if (header.width) {
                style.width = header.width;
            }
            if (header.maxWidth) {
                style.maxWidth = header.maxWidth;
            }
            return style;
        },
        onPlaceholderAction(action) {
            if (action == 'reset-filters') {
                if (this.$refs.tableControls) {
                    this.$refs.tableControls.resetAll();
                } else {
                    this.$emit('on-placeholder-action', action);
                }
            } else {
                this.$emit('on-placeholder-action', action);
            }
        },
        async runBulkAction({ action, payload }) {
            try {
                const result = await this.bulkAction({ action, payload });
                if (!result) {
                    return;
                }
                if (!result.errors?.length) {
                    this.$emit('input', []);
                    this.addAlert(this.$translate("messages.bulkActionCompleted"));
                }
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.bulkActionFailed"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
        clearSelection() {
            this.$emit("input", []);
        },
        onSelectionInput(selected) {
            let next = selected;
            if (this.selectableKey && Array.isArray(selected)) {
                next = selected.filter((item) => item[this.selectableKey]);
            }
            this.$emit('input', next);
        },
        toggleItem(item, selected) {
            if (this.selectableKey && !item[this.selectableKey]) {
                return;
            }
            if (selected) {
                this.$emit("on-row-selected", item);
                // Check if item already exists in the list
                const existingItem = (this.value || []).find(
                    (selected_item) => selected_item[this.itemKey] == item[this.itemKey]
                );
                // Only add if it doesn't already exist
                if (!existingItem) {
                    let new_list = (this.value || []).concat(item);
                    this.$emit("input", new_list);
                }
            } else {
                this.$emit("on-row-deselected", item);
                let new_list = this.value.filter(
                    (selected_item) => selected_item[this.itemKey] != item[this.itemKey]
                );
                this.$emit("input", new_list);
            }
        },
        displayColName(header) {
            if (["xs"].indexOf(this.$vuetify.breakpoint.name) == -1) {
                return false;
            }
            return header.type != "actions" && header.type != "getactions";
        },
        onErrorAction($event) {
            if ($event == "retry") {
                this.$emit("retry");
            }
        },
        onFiltersChange(filters) {
            filters.offset = 0;
            this.page = 1;

            this.$emit("on-filters-change", filters);
        },
        onViewApply(viewFilters) {
            const merged = {
                ...this.filters,
                ...viewFilters,
                offset: 0,
            };
            this.onFiltersChange(merged);
        },
        onViewReset() {
            if (this.$refs.tableControls) {
                this.$refs.tableControls.resetAll();
            }
        },
        onColumnsStateChange({ columns }) {
            this.orderedColumns = columns;
        },
        getOrdering() {
            if (!this.sortBy || !this.sortBy.length || !this.sortBy[0]) {
                return null;
            }
            const column = this.effectiveColumns.find(column => column.field === this.sortBy[0]);
            let sortField = column?.sortField || column?.field || this.sortBy[0];

            let sortDesc = this.sortDesc.length ? this.sortDesc[0] : false;
            if (Array.isArray(sortField)) {
                sortField = sortField[0];
            }

            if (!sortField) {
                return null;
            }
            sortField = sortField.slice(sortField.lastIndexOf("-") + 1);
            return (sortDesc ? "-" : "") + sortField;
        },
        onRowDragStart(event, index) {
            if (!this.enableRowReorder) {
                event.preventDefault();
                return;
            }
            this.rowDragIndex = index;
            this.rowReorderDragging = true;
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(index));
                const row = event.currentTarget && event.currentTarget.closest
                    ? event.currentTarget.closest("tr")
                    : null;
                if (row && event.dataTransfer.setDragImage) {
                    event.dataTransfer.setDragImage(row, 24, 16);
                }
            }
        },
        onRowDragOver(event, index) {
            if (!this.enableRowReorder || this.rowDragIndex === null) {
                return;
            }
            this.rowDragOverIndex = index;
            const row = event.currentTarget;
            if (row && typeof row.getBoundingClientRect === "function") {
                const rect = row.getBoundingClientRect();
                this.rowDragPlaceAfter =
                    event.clientY > rect.top + rect.height / 2;
            } else {
                this.rowDragPlaceAfter = false;
            }
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = "move";
            }
        },
        onRowDrop(event, index) {
            if (!this.enableRowReorder || this.rowDragIndex === null) {
                return;
            }
            const fromIndex = this.rowDragIndex;
            // Prefer the live pointer position on drop (more accurate than last dragover).
            let placeAfter = this.rowDragPlaceAfter;
            const row = event.currentTarget;
            if (row && typeof row.getBoundingClientRect === "function") {
                const rect = row.getBoundingClientRect();
                placeAfter = event.clientY > rect.top + rect.height / 2;
            }
            this.rowDragIndex = null;
            this.rowDragOverIndex = null;
            this.rowDragPlaceAfter = false;
            if (!Array.isArray(this.rows)) {
                return;
            }
            // Insert before (top half) or after (bottom half) the drop target.
            // After removing fromIndex, later indices shift left by one.
            let targetIndex = placeAfter ? index + 1 : index;
            if (fromIndex < targetIndex) {
                targetIndex -= 1;
            }
            if (targetIndex === fromIndex) {
                return;
            }
            const reordered = this.rows.slice();
            const [moved] = reordered.splice(fromIndex, 1);
            reordered.splice(targetIndex, 0, moved);
            this.$emit(
                "on-row-reorder",
                reordered.map((row) => row[this.itemKey]),
            );
        },
        onRowDragEnd() {
            this.rowDragIndex = null;
            this.rowDragOverIndex = null;
            this.rowDragPlaceAfter = false;
            this.$nextTick(() => {
                this.rowReorderDragging = false;
            });
        },
        handleAction(action, item) {
            this.$emit("on-row-action", {
                action: action.action,
                payload: action.payload,
                item,
                params: action.params,
            });
        },
        getValue(obj, header) {
            let value = obj;
            if (header.field && header.field != "*") {
                const parts = Array.isArray(header.field) ? header.field : [header.field];

                let properties = [];
                parts.forEach((part) => {
                    part.split(".").forEach((dotsplit) => {
                        dotsplit.split("__").forEach((underscoresplit) => {
                            properties.push(underscoresplit);
                        });
                    });
                });
                value = properties.reduce((prev, curr) => prev && prev[curr], obj);
            }
            return value;
        },
        percentValue(value) {
            return new Decimal(value).times(100);
        },
        resolve(obj, header, final_field) {
            if (!header) {
                return;
            }
            let value = this.getValue(obj, header);
            if (header.render) {
                return header.render(value);
            }

            if (header.type == "datetime" && value && value.length) {
                if (["xs", "sm"].indexOf(this.$vuetify.breakpoint.name) == -1) {
                    value = Vue.moment(value).format(header.dateFormat || "LLL");
                } else {
                    value = Vue.moment(value).format("L");
                }
            }
            if (header.type == "humandate" && value && value.length) {
                if (["xs", "sm"].indexOf(this.$vuetify.breakpoint.name) == -1) {
                    value = Vue.moment(value).format(header.dateFormat || "LL");
                } else {
                    value = Vue.moment(value).format("L");
                }
            }
            if (value === null || value === undefined) {
                return null;
            }
            if (final_field) {
                return value[final_field];
            }

            return value;
        },
        isSelected(item) {
            return !!this.value?.find(
                (selected_item) => selected_item[this.itemKey] == item[this.itemKey]
            );
        },
        applyStickyHeaders() {
            // Find the v-data-table element
            const dataTable = this.$el.querySelector('.v-data-table');
            if (!dataTable) return;
            
            // Find the thead and th elements
            const thead = dataTable.querySelector('thead');
            if (!thead) return;
            
            const thElements = thead.querySelectorAll('th');
            if (!thElements || thElements.length === 0) return;
            
            // Get headers with sticky property
            const headers = this.headers;
            
            // Apply sticky-column class to th elements based on header.sticky
            thElements.forEach((th, index) => {
                if (headers[index] && headers[index].sticky) {
                    th.classList.add('sticky-column');
                } else {
                    th.classList.remove('sticky-column');
                }
            });
        },
    },
    i18n: {
        messages: {
            en: {
                displayFilters: "Display filters",
                selectionItemInstructions: "Choose an action to perform on this item.",
                selectionItemsInstructions: "Choose an action to perform on these items.",
                youSelected: "You selected",
                item: "item",
                items: "items",
                selectAction: "Select an action",
                clearSelection: "Cancel",
                labels: {
                    itemsPerPage: "Items per page",
                    orderFilter: "Order filter",
                },
                buttons: {
                    retry: "Retry",
                    resetFilters: "Reset filters",
                },
                messages: {
                    errorLoadingData: "Error loading data",
                    errorLoadingDataDescription:
                        "An error occurred while loading the data. Please try again.",
                    noResultFound: "No result found",
                    noResultFoundDescription: "No data matches your search criteria.",
                    bulkActionCompleted: "Bulk action completed",
                    bulkActionFailed: "Bulk action failed",
                    bulkActionFailedDescription:
                        "An error occurred while processing the bulk action.",
                },
                month: "%{months} month",
                months: "%{months} months",
                yes: "Yes",
                no: "No",
            },
            fr: {
                displayFilters: "Afficher les filtres",
                selectionItemInstructions:
                    "Choisissez une action à effectuer sur cet élément.",
                selectionItemsInstructions:
                    "Choisissez une action à effectuer sur ces éléments.",
                youSelected: "Vous avez sélectionné",
                item: "élément",
                items: "éléments",
                selectAction: "Sélectionner une action",
                clearSelection: "Annuler",
                labels: {
                    itemsPerPage: "Éléments par page",
                    orderFilter: "Filtre de commande",
                },
                buttons: {
                    retry: "Réessayer",
                    resetFilters: "Réinitialiser les filtres",
                },
                messages: {
                    errorLoadingData: "Erreur lors du chargement des données",
                    errorLoadingDataDescription:
                        "Une erreur s'est produite lors du chargement des données. Veuillez réessayer.",
                    noResultFound: "Aucun résultat trouvé",
                    noResultFoundDescription:
                        "Aucune donnée ne correspond à vos critères de recherche.",
                    bulkActionCompleted: "Action groupée terminée",
                    bulkActionFailed: "Échec de l'action groupée",
                    bulkActionFailedDescription:
                        "Une erreur s'est produite lors du traitement de l'action groupée.",
                },
                month: "%{months} mois",
                months: "%{months} mois",
                yes: "Oui",
                no: "Non",
            },
        },
    },
};
</script>

<style lang="scss">
.tablewrapper {
    padding: 16px;

    /* Filter bar: no horizontal padding — inherit only the wrapper inset. */
    > .controls {
        padding-top: 0 !important;
        padding-right: 0 !important;
        padding-bottom: 16px !important;
        padding-left: 0 !important;
    }

    .table-bulk-selection-wrapper {
        margin-bottom: 16px;
        min-width: 0;
        max-width: 100%;
    }

    /* Bulk selection: mobile stacks; larger screens keep wrapping text beside actions. */
    .table-bulk-selection-bar {
        min-width: 0;
        max-width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        padding-top: 12px !important;
        padding-bottom: 12px !important;

        > b.text-overline {
            display: none;
        }

        > .table-bulk-selection-bar__message {
            flex: 1 1 100%;
            width: 100%;
            min-width: 0;
            text-align: center;
            white-space: normal;
            overflow-wrap: anywhere;

            span {
                white-space: normal;
            }
        }

        > .table-bulk-selection-bar__clear,
        > .table-bulk-selection-bar__action {
            flex: 1 1 100%;
            width: 100%;
            min-width: 0;
            margin: 0 !important;
        }

        > .table-bulk-selection-bar__clear ::v-deep .v-btn,
        > .table-bulk-selection-bar__action ::v-deep .v-btn {
            width: 100%;
        }

        @media (min-width: 600px) {
            flex-wrap: nowrap;

            > .table-bulk-selection-bar__message {
                flex: 1 1 auto;
                width: auto;
                text-align: start;
            }

            > .table-bulk-selection-bar__clear,
            > .table-bulk-selection-bar__action {
                flex: 0 0 auto;
                width: auto;
            }

            > .table-bulk-selection-bar__clear ::v-deep .v-btn,
            > .table-bulk-selection-bar__action ::v-deep .v-btn {
                width: auto;
            }
        }

    }

    td,
    th {
        white-space: nowrap;
    }
    td.table-cell-wrap {
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
        vertical-align: middle;
    }
    .actions {
        display: flex;
        align-items: center;
        & > * + * {
            margin-left: 10px;
        }
    }
    .noresult--title {
        font-family: "Inter", sans-serif;
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        line-height: 16px;
    }
    
    &.table-bordered {
        ::v-deep .v-data-table {
            border: 1px solid rgba(0, 0, 0, 0.12);
            
            table {
                border-collapse: separate;
                border-spacing: 0;
                width: 100%;
                
                thead {
                    tr {
                        th {
                            border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
                            border-bottom: 2px solid rgba(0, 0, 0, 0.12) !important;
                            border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
                            border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
                            
                            &:first-child {
                                border-left: none !important;
                            }
                            
                            &:last-child {
                                border-right: none !important;
                            }
                        }
                    }
                }
                
                tbody {
                    tr {
                        td {
                            border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
                            border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
                            border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
                            
                            &:first-child {
                                border-left: none !important;
                            }
                            
                            &:last-child {
                                border-right: none !important;
                            }
                        }
                        
                        &:last-child {
                            td {
                                border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
                            }
                        }
                    }
                }
            }
        }
        
        .table-bordered-cell {
            border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
            &:first-child {
                border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
            }
        }
    }
    
    &.table-no-cell-padding {
        .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
        .v-data-table > .v-data-table__wrapper > table > tbody > tr > th {
            padding: 0 !important;
        }
    }
    
    .sticky-column {
        position: sticky;
        left: 0;
        background-color: white;
        z-index: 2;
        
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 1px;
            background-color: rgba(0, 0, 0, 0.12);
        }
    }
    
    // Apply sticky to th with sticky-column class
    .v-data-table {
        thead {
            tr {
                th.sticky-column {
                    position: sticky;
                    left: 0;
                    background-color: #fff;
                    z-index: 3;
                    border-right: none !important; // Remove border-right to avoid double border
                    
                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        width: 1px;
                        background-color: rgba(0, 0, 0, 0.12);
                    }
                }
                
                // Remove border-left from the column following a sticky column
                th.sticky-column + th {
                    border-left: none !important;
                }
            }
        }
        
        tbody {
            tr {
                td.sticky-column {
                    border-right: none !important; // Remove border-right to avoid double border
                }
                
                // Remove border-left from the column following a sticky column
                td.sticky-column + td {
                    border-left: none !important;
                }
            }
        }
    }
    
    &.table-compact-selection thead th:first-child {
        padding-left: 8px !important;
        padding-right: 0 !important;
        width: 36px !important;
        min-width: 36px !important;
        max-width: 36px !important;
    }
    &.table-compact-selection thead th:first-child .v-input--selection-controls__input {
        margin-right: 0;
    }
    td.table-compact-select-cell, th.table-compact-select-cell {
        padding-left: 8px !important;
        padding-right: 0 !important;
        width: 36px !important;
    }
    td.table-compact-action-cell, th.table-compact-action-cell {
        padding-left: 0 !important;
        padding-right: 4px !important;
    }
    .table-row-drag-handle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        min-height: 32px;
        cursor: grab;
        color: rgba(0, 0, 0, 0.38);
        user-select: none;
        -webkit-user-drag: element;

        .v-icon {
            pointer-events: none;
        }

        &:active {
            cursor: grabbing;
        }
    }

    th.table-row-reorder-col,
    td.table-row-reorder-col {
        width: 40px;
        min-width: 40px;
        max-width: 40px;
        padding-left: 8px !important;
        padding-right: 4px !important;
    }

    tr.table-row--dragging {
        opacity: 0.55;
    }

    tr.table-row--drag-over-before td {
        box-shadow: inset 0 2px 0 0 var(--v-primary-base, #1976d2);
    }

    tr.table-row--drag-over-after td {
        box-shadow: inset 0 -2px 0 0 var(--v-primary-base, #1976d2);
    }

    // Disable row hover effect
    &.table-disable-row-hover {
        .v-data-table__wrapper > table > tbody > tr:hover {
            background-color: transparent !important;
        }
    }

    tr.table-summary-row td {
        border-top: thin solid rgba(0, 0, 0, 0.12) !important;
        background-color: rgba(0, 0, 0, 0.03) !important;
        font-size: 0.875rem;
    }

    .v-data-table__wrapper {
        overscroll-behavior-x: contain;
    }

    @media (max-width: 599px) {
        .v-data-table__wrapper {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }

        td.table-cell-wrap {
            white-space: normal !important;
            overflow-wrap: break-word;
            word-break: break-word;
        }
    }

    // Fix pagination on mobile
    .v-data-footer {
        @media (max-width: 599px) {
            .v-data-footer__pagination, .v-data-footer__icons-after .v-btn, .v-data-footer__icons-before .v-btn{
                margin-left: 0 !important;
                margin-right: 0 !important;
            }
        }
    }
}

.tablewrapper .v-data-table > .v-data-table__wrapper > table > tbody > tr > td { font-size: 11px; }
.tablewrapper .v-data-table > .v-data-table__wrapper > table > thead > tr > th { font-size: 11px; }
.tablewrapper .v-chip.v-size--small { font-size: 11px; }
.tablewrapper .table-cell-secondary { font-size: 11px; }

/* Shared readable table surface, including multi-line secondary information. */
.tablewrapper > .v-data-table { border:1px solid #e3e7ef; border-radius:10px; overflow:hidden; }
.tablewrapper > .v-data-table > .v-data-table__wrapper > table > thead > tr > th { background:#f8f9fc; color:#20243a; font-weight:600; border-bottom:1px solid #dce1eb; }
.tablewrapper > .v-data-table > .v-data-table__wrapper > table > tbody > tr > td { border-bottom-color:#e3e7ef; }
.tablewrapper:not(.table-no-cell-padding) > .v-data-table:not(.v-data-table--dense) > .v-data-table__wrapper > table > tbody > tr > td { padding-top:10px; padding-bottom:10px; }
.tablewrapper .table-cell-secondary { color:#7a8292; font-size:11px; line-height:1.5; margin-top:4px; font-weight:400; }
</style>
